import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  Mail,
  Trash2,
  UserPlus,
  Copy,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchProjectMembers,
  inviteCollaborator,
  removeProjectMember,
  revokeProjectInvite,
} from "@/api";
import { formatApiErrorDetail } from "@/contexts/AuthContext";

export default function CollaboratorsDialog({ open, onOpenChange, projectId, projectName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const role = "editor"; // collaborators are always invited as editors

  async function load() {
    setLoading(true);
    try {
      const r = await fetchProjectMembers(projectId);
      setData(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setData({ members: [], pending_invites: [] });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open || !projectId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  async function invite() {
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      const res = await inviteCollaborator(projectId, { email: email.trim(), role });
      if (res?.email_sent) {
        toast.success(`Invitation emailed to ${email}`);
      } else if (res?.accept_url) {
        try {
          await navigator.clipboard.writeText(res.accept_url);
        } catch {
          /* noop */
        }
        toast.success("Invite link copied to clipboard");
      } else {
        toast.success("Invitation created");
      }
      setEmail("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(member) {
    if (!window.confirm(`Remove ${member.email} from this project?`)) return;
    try {
      await removeProjectMember(projectId, member.user_id);
      toast.success(`Removed ${member.email}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function revokeInvite(inv) {
    try {
      await revokeProjectInvite(projectId, inv.id);
      toast.success("Invite revoked");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  function copyInviteLink(inv) {
    const url = `${window.location.origin}/accept-invite?token=${inv.token}`;
    try {
      navigator.clipboard.writeText(url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl"
        data-testid="collaborators-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl">
            <Users className="w-5 h-5" strokeWidth={1.5} />
            Collaborators
          </DialogTitle>
          {projectName && (
            <p className="text-xs text-stone-500 mt-1">{projectName}</p>
          )}
        </DialogHeader>

        <div className="border border-stone-200 rounded-xl p-4 space-y-3">
          <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
            Invite by email
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="collaborator@studio.com"
              data-testid="collab-invite-email"
              className="h-10 flex-1"
            />
            <Button
              onClick={invite}
              disabled={busy}
              data-testid="collab-invite-submit"
              className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} />
              )}
              Invite
            </Button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
          </div>
        )}

        {data && (
          <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold mb-2">
                Members ({data.members?.length || 0})
              </p>
              {data.members?.length ? (
                <ul className="space-y-2" data-testid="collab-members-list">
                  {data.members.map((m) => (
                    <li
                      key={m.user_id}
                      data-testid={`collab-member-${m.email}`}
                      className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg"
                    >
                      <div className="w-8 h-8 rounded-full bg-stone-900 text-white text-[11px] font-semibold flex items-center justify-center">
                        {(m.name || m.email).slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-stone-900 truncate">
                          {m.name || m.email}
                        </div>
                        <div className="text-xs text-stone-500 truncate">
                          {m.email}
                        </div>
                      </div>
                      <span
                        data-testid={`collab-member-role-${m.email}`}
                        className="text-xs text-stone-500 capitalize px-2"
                      >
                        {m.role || "editor"}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeMember(m)}
                        data-testid={`collab-member-remove-${m.email}`}
                        className="rounded-full text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-stone-400">No members yet.</p>
              )}
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold mb-2">
                Pending invites ({data.pending_invites?.length || 0})
              </p>
              {data.pending_invites?.length ? (
                <ul className="space-y-2" data-testid="collab-invites-list">
                  {data.pending_invites.map((inv) => (
                    <li
                      key={inv.id}
                      data-testid={`collab-invite-${inv.email}`}
                      className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-100 rounded-lg"
                    >
                      <Mail className="w-4 h-4 text-amber-700" strokeWidth={1.5} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-stone-900 truncate">
                          {inv.email}
                        </div>
                        <div className="text-xs text-stone-500">
                          {inv.role} · sent{" "}
                          {inv.created_at
                            ? new Date(inv.created_at).toLocaleDateString()
                            : ""}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyInviteLink(inv)}
                        data-testid={`collab-invite-copy-${inv.email}`}
                        className="rounded-full"
                      >
                        <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => revokeInvite(inv)}
                        data-testid={`collab-invite-revoke-${inv.email}`}
                        className="rounded-full text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-stone-400">No pending invites.</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
